import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import CommonWrapper from '../../components/ui_components/CommonComponentsStructureCss';
import { useAppSelector } from '../../store/hooks';
import { apiGetExpansions } from '../../api/expansionApi';
import { apiGetCards } from '../../api/cardApi';
import { apiGetMyOwnedCards } from '../../api/userCardApi';
import type { Expansion } from '../../types/models/expansion';
import type { Card, CardRarity } from '../../types/models/card';

const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';
const toUrl = (path: string | null | undefined) =>
  path ? (path.startsWith('http') ? path : storageUrl + path) : null;

const RARITY_COLOR: Record<CardRarity, string> = {
  common: 'rgba(255,255,255,0.5)',
  rare: '#4a9eff',
  epic: '#c27ff5',
  legendary: 'gold',
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const PageInner = styled.div`
  padding: 40px 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: gold;
  margin: 0 0 4px;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0 0 36px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
`;

const Card = styled.div`
  background-color: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const CardBanner = styled.div`
  height: 220px;
  background-color: #111;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
`;

const BannerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: 0.7;
`;

const BannerPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.1);
  font-size: 13px;
`;

const CompleteBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: gold;
  color: #000;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 20px;
  letter-spacing: 0.3px;
`;

const CardBody = styled.div`
  padding: 16px 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const ExpName = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  margin: 0;
`;

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ProgressTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const ProgressCount = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #fff;
`;

const ProgressPct = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
`;

const ProgressTrack = styled.div`
  height: 6px;
  background-color: rgba(255, 255, 255, 0.07);
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number; $complete: boolean }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background-color: ${({ $complete }) => ($complete ? 'gold' : 'rgba(255,215,0,0.6)')};
  border-radius: 4px;
  transition: width 0.4s ease;
`;

const RarityRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const RarityChip = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);

  &::before {
    content: '';
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: ${({ $color }) => $color};
    flex-shrink: 0;
  }
`;

const GuestNote = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.35);
  text-align: center;
  margin: 0 0 28px;
  font-style: italic;
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 48px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const ExpansionsProgressPage: React.FC = () => {
  const [expansions, setExpansions] = useState<Expansion[]>([]);
  const [allCards, setAllCards] = useState<Card[]>([]);
  const [ownedMap, setOwnedMap] = useState<Map<number, number>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = useAppSelector(
    (state) => state.auth.isAuthenticated || state.auth.userLoggedState !== null
  );

  useEffect(() => {
    let isMounted = true;
    const fetchExpansions = apiGetExpansions()
      .then((data) => {
        if (isMounted) {
          const list: Expansion[] = Array.isArray(data) ? data : (data as any)?.data ?? [];
          setExpansions(list);
        }
      })
      .catch(() => { if (isMounted) setError('Errore durante il caricamento delle espansioni.'); });

    const fetchCards = apiGetCards()
      .then((data) => {
        if (isMounted) {
          const list: Card[] = Array.isArray(data) ? data : (data as any)?.data ?? [];
          setAllCards(list);
        }
      })
      .catch(() => {});

    const fetchOwned = isAuthenticated
      ? apiGetMyOwnedCards()
          .then((data) => {
            if (isMounted) {
              const list = Array.isArray(data) ? data : (data as any)?.data ?? [];
              const map = new Map<number, number>();
              list.forEach((e: { card_id: number; quantity: number }) => map.set(e.card_id, e.quantity));
              setOwnedMap(map);
            }
          })
          .catch(() => {})
      : Promise.resolve();

    Promise.all([fetchExpansions, fetchCards, fetchOwned]).finally(() => {
      if (isMounted) setIsLoading(false);
    });

    return () => { isMounted = false; };
  }, [isAuthenticated]);

  const expansionStats = useMemo(() => {
    return expansions.map((exp) => {
      const cards = allCards.filter((c) => c.expansions.some((e) => e.id === exp.id));
      const total = cards.length;
      const owned = cards.filter((c) => ownedMap.has(c.id)).length;
      const pct = total > 0 ? Math.round((owned / total) * 100) : 0;
      const complete = total > 0 && owned === total;

      const byRarity = (['common', 'rare', 'epic', 'legendary'] as CardRarity[])
        .map((r) => {
          const rTotal = cards.filter((c) => c.rarity === r).length;
          const rOwned = cards.filter((c) => c.rarity === r && ownedMap.has(c.id)).length;
          return { rarity: r, owned: rOwned, total: rTotal };
        })
        .filter(({ total: t }) => t > 0);

      return { exp, total, owned, pct, complete, byRarity };
    });
  }, [expansions, allCards, ownedMap]);

  return (
    <CommonWrapper>
      <PageInner>
        <PageTitle>Espansioni</PageTitle>
        <PageSubtitle>Progresso di completamento per ogni espansione</PageSubtitle>

        {!isAuthenticated && (
          <GuestNote>Accedi per vedere quante carte possiedi per ogni espansione.</GuestNote>
        )}

        {isLoading && <StatusText>Caricamento...</StatusText>}
        {error && <StatusText style={{ color: '#dc3545' }}>{error}</StatusText>}
        {!isLoading && !error && expansions.length === 0 && (
          <StatusText>Nessuna espansione disponibile.</StatusText>
        )}

        {!isLoading && !error && expansionStats.length > 0 && (
          <Grid>
            {expansionStats.map(({ exp, total, owned, pct, complete, byRarity }) => {
              const img = toUrl(exp.expansion_path);
              return (
                <Card key={exp.id}>
                  <CardBanner>
                    {img
                      ? <BannerImg src={img} alt={exp.name} />
                      : <BannerPlaceholder>{exp.name}</BannerPlaceholder>
                    }
                    {complete && <CompleteBadge>Completa</CompleteBadge>}
                  </CardBanner>

                  <CardBody>
                    <ExpName>{exp.name}</ExpName>

                    {total > 0 && (
                      <ProgressSection>
                        <ProgressTopRow>
                          <ProgressCount>
                            {isAuthenticated ? `${owned} / ${total}` : `${total} carte`}
                          </ProgressCount>
                          {isAuthenticated && <ProgressPct>{pct}%</ProgressPct>}
                        </ProgressTopRow>
                        {isAuthenticated && (
                          <ProgressTrack>
                            <ProgressFill $pct={pct} $complete={complete} />
                          </ProgressTrack>
                        )}
                      </ProgressSection>
                    )}

                    {isAuthenticated && byRarity.length > 0 && (
                      <RarityRow>
                        {byRarity.map(({ rarity, owned: ro, total: rt }) => (
                          <RarityChip key={rarity} $color={RARITY_COLOR[rarity]}>
                            {ro}/{rt}
                          </RarityChip>
                        ))}
                      </RarityRow>
                    )}
                  </CardBody>
                </Card>
              );
            })}
          </Grid>
        )}
      </PageInner>
    </CommonWrapper>
  );
};

export default ExpansionsProgressPage;
